import { doodBlockConfig, propertyValue } from './dood-block-config';
import {
  buildOrderedBlockDescriptor,
  type DescriptorFieldOrderItem,
} from './descriptor-field-order';
import type { IBlockDescriptor } from './descriptor';

/** Serializable product row used in kiosk cart products preview scenarios. */
export type KioskCartProductsListPreviewProduct = {
  id: string;
  _id: string;
  shop_id: string;
  name: string;
  description?: string;
  price: number;
  final_price: number;
  vat: string;
  additions_group: string;
  categories: readonly unknown[];
  additions: { products: readonly unknown[] };
  images?: {
    thumbnail?: { url: string; width?: number; height?: number };
  };
};

/** Serializable cart line item used in kiosk cart products preview scenarios. */
export type KioskCartProductsListPreviewCartItem = {
  item: {
    id: number;
    quantity: number;
    additions?: string[];
    products?: readonly { id: string; quantity: number; additions?: string[] }[];
  };
  product: KioskCartProductsListPreviewProduct;
  unit_price: number;
};

/** Serializable shop header for a reconstructed cart group. */
export type KioskCartProductsListPreviewShop = {
  id: string;
  name: string;
  slug: string;
  products: readonly KioskCartProductsListPreviewProduct[];
};

/** Serializable reconstructed cart group (one shop + its line items). */
export type KioskCartProductsListPreviewCart = {
  shop: KioskCartProductsListPreviewShop;
  items: readonly KioskCartProductsListPreviewCartItem[];
};

/**
 * Serializable preview content for `KioskCartProductsListBlockComponent`.
 * Kept in content-builder so dashboard + webapp share the same transport shape.
 */
export type KioskCartProductsListBlockScenarioContent = {
  reconstructedCart: readonly KioskCartProductsListPreviewCart[] | undefined;
  reconstructedShareOrderBasket: readonly unknown[];
  allProducts: readonly KioskCartProductsListPreviewProduct[];
  currency: string;
};
export const KIOSKCARTPRODUCTSLISTBLOCK_LEGACY_FIELD_ORDER = [
  'title',
  'primary_button_background_color',
  'primary_button_text_color',
  'background_color',
  'text_color',
] as const satisfies readonly DescriptorFieldOrderItem[];

const {
  kioskCartProductsListBlockDescriptor: _generatedKioskCartProductsListBlockDescriptor,
  kioskCartProductsListBlockBlocDefinition,
  kioskCartProductsListBlockDashboardLayout,
  kioskCartProductsListBlockConfig,
  kioskCartProductsListBlockResolveConfig,
  kioskCartProductsListBlockProperties,
  kioskCartProductsListBlockDefinitionSetupScenarios,
} = doodBlockConfig(
  {
    name: 'Kiosk Cart - Products List',
    componentType: 'KioskCartProductsListBlockComponent',
    category: 'kiosk',
    description: 'Kiosk Cart - Products List.',
    keywords: [],
    allowedIn: [
      { bloc: 'KioskCartPageComponent', slot: 'body' },
    ],


    sharedFields: {},
  },
  () => ({
    title: propertyValue({
      type: 'text',
      value: '',
      options: {
        label: 'pages.blocks.kiosk-cart-products-list.title.label',
      },
    }),
    primary_button_background_color: propertyValue({
      type: 'color',
      value: { type: 'palette', value: 'neutral-600' },
      options: {
        label: 'pages.blocks.kiosk-cart-products-list.primary-button-background-color.label',
      },
    }),
    primary_button_text_color: propertyValue({
      type: 'color',
      value: { type: 'palette', value: 'neutral-50' },
      options: {
        label: 'pages.blocks.kiosk-cart-products-list.primary-button-text-color.label',
      },
    }),
    secondary_button_background_color: propertyValue({
      type: 'color',
      value: { type: 'palette', value: 'neutral-50' },
      options: {
        label: 'pages.blocks.kiosk-cart-products-list.secondary-button-background-color.label',
      },
    }),
    secondary_button_text_color: propertyValue({
      type: 'color',
      value: { type: 'palette', value: 'neutral-600' },
      options: {
        label: 'pages.blocks.kiosk-cart-products-list.secondary-button-text-color.label',
      },
    }),
    background_color: propertyValue({
      type: 'color',
      value: { type: 'hex', value: '#FFFFFF' },
      options: {
        label: 'pages.blocks.kiosk-cart-products-list.background-color.label',
      },
    }),
    text_color: propertyValue({
      type: 'color',
      value: { type: 'hex', value: '#000' },
      options: {
        label: 'pages.blocks.kiosk-cart-products-list.text-color.label',
      },
    }),
  }),
  () => ({
    omitFields: [
      'secondary_button_background_color',
      'secondary_button_text_color',
    ] as const,
    tabs: [
      {
        label: 'pages.blocks.kiosk-cart-products-list.tab.content.label',
        fieldsets: [
          {
            legend: 'pages.blocks.kiosk-cart-products-list.fieldset.title.label',
            fields: ['title'],
          },
          {
            legend: 'pages.blocks.kiosk-cart-products-list.fieldset.list.label',
            fields: ['background_color', 'text_color'],
          },
          {
            legend: 'pages.blocks.kiosk-cart-products-list.fieldset.quantity.label',
            fields: [
              'primary_button_background_color',
              'primary_button_text_color',
            ],
          },
        ],
      },
    ],
  })
);

export function kioskCartProductsListBlockDescriptor(): IBlockDescriptor;
export function kioskCartProductsListBlockDescriptor(
  fieldOrder: readonly DescriptorFieldOrderItem[],
): IBlockDescriptor;
export function kioskCartProductsListBlockDescriptor(
  fieldOrder?: readonly DescriptorFieldOrderItem[],
): IBlockDescriptor {
  if (fieldOrder === undefined) {
    return _generatedKioskCartProductsListBlockDescriptor;
  }
  return buildOrderedBlockDescriptor(
    _generatedKioskCartProductsListBlockDescriptor,
    kioskCartProductsListBlockProperties,
    fieldOrder,
  );
}

export {
  kioskCartProductsListBlockBlocDefinition,
  kioskCartProductsListBlockDashboardLayout,
  kioskCartProductsListBlockProperties,
  kioskCartProductsListBlockConfig,
  kioskCartProductsListBlockResolveConfig,
  kioskCartProductsListBlockDefinitionSetupScenarios,
};

export type KioskCartProductsListBlockConfig = typeof kioskCartProductsListBlockConfig;
